import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WORK_ORDER, GET_PROPERTY_RESERVATIONS } from '../graphql/queries';
import { UPDATE_WORK_ORDER_STATUS, DELETE_WORK_ORDER, UPDATE_WORK_ORDER, RESTORE_WORK_ORDER } from '../graphql/mutations';
import { format } from 'date-fns';
import { formatDueDate } from '../utils/date-utils';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import AssignWorkOrderModal from '../components/work-orders/AssignWorkOrderModal';
import CompleteWorkOrderModal from '../components/work-orders/CompleteWorkOrderModal';
import { WorkOrderCommentThread } from '../components/work-orders/comments';
import WorkOrderExpensesSection from '../components/work-orders/WorkOrderExpensesSection';
import WorkOrderTimeTracker from '../components/work-orders/WorkOrderTimeTracker';
import { WorkOrderGPSTracker } from '../components/work-orders/gps';
import { UserRoleLabels } from '../utils/enums';
import VideoUploader from '../components/work-orders/VideoUploader';
import VideoPlayerModal from '../components/work-orders/VideoPlayerModal';
import WorkOrderFlagManager from '../components/work-orders/WorkOrderFlagManager';
import PropertyManagementDisplay from '../components/properties/PropertyManagementDisplay';
import usePropertyManagementDisplay from '../hooks/usePropertyManagementDisplay';
import { CustomFlagIndicator, WorkOrderBadge, WorkOrderUrgentBadge } from '../components/job-indicators';
import { WorkOrderType, getWorkOrderTypeOption } from '../utils/work-order-types';
import { uploadWorkOrderPhoto } from '../utils/work-order-photo-upload';

const WORK_ORDER_TYPE_BADGE_CLASSES = {
  [WorkOrderType.GUEST_IMPACT]: 'bg-blue-100 text-blue-800',
  [WorkOrderType.PROPERTY_CARE]: 'bg-amber-100 text-amber-800',
  [WorkOrderType.HOUSEKEEPING]: 'bg-emerald-100 text-emerald-800',
  [WorkOrderType.FEEDBACK]: 'bg-purple-100 text-purple-800',
};

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  try {
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

const createDueDateValue = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};

const formatPropertyAddress = (property) => {
  if (!property) return '';
  return [property.address, property.city, property.state, property.zip]
    .filter(Boolean)
    .join(', ');
};

export default function WorkOrderDetail({ workOrderId: propWorkOrderId, isSlidePanel = false, onDataChange } = {}) {
  const { id: paramId } = useParams();
  const id = propWorkOrderId || paramId;
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading, refreshUser } = useAuth();
  const [photoFiles, setPhotoFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  // Video states
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isDueDateEditorOpen, setIsDueDateEditorOpen] = useState(false);
  const [dueDateDraft, setDueDateDraft] = useState('');
  const [isSavingDueDate, setIsSavingDueDate] = useState(false);
  const [isQuickCompleting, setIsQuickCompleting] = useState(false);
  const dueDateInputRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(GET_WORK_ORDER, {
    variables: { id },
    fetchPolicy: 'network-only',
  });
  const { team: propertyManagementTeam, managers: propertyManagers } = usePropertyManagementDisplay(data?.workOrder?.property);

  // Fetch current reservations for this property to get guest contact info
  const { data: propertyReservationsData } = useQuery(GET_PROPERTY_RESERVATIONS, {
    variables: { propertyId: data?.workOrder?.property?.id },
    skip: !data?.workOrder?.property?.id,
    fetchPolicy: 'network-only',
  });

  const [updateWorkOrderStatus] = useMutation(UPDATE_WORK_ORDER_STATUS, {
    onCompleted: () => { refetch(); onDataChange?.(); }
  });

  const [deleteWorkOrder] = useMutation(DELETE_WORK_ORDER, {
    onCompleted: () => {
      toast.success('Work order deleted successfully');
      onDataChange?.();
      if (!isSlidePanel) navigate('/dashboard/work-orders');
    },
    update: (cache) => {
      cache.evict({ id: cache.identify({ __typename: 'WorkOrder', id }) });
      cache.gc();
    }
  });

  const [updateWorkOrder] = useMutation(UPDATE_WORK_ORDER, {
    onCompleted: () => { onDataChange?.(); }
  });

  const [restoreWorkOrder] = useMutation(RESTORE_WORK_ORDER, {
    onCompleted: () => {
      toast.success('Work order restored successfully');
      refetch();
      onDataChange?.();
    }
  });

  useEffect(() => {
    if (isSlidePanel) return;
    if (!authLoading && !currentUser) {
      refreshUser().catch(() => {});
    }
  }, [currentUser, authLoading, isSlidePanel]);

  useEffect(() => {
    setDueDateDraft(formatDateForInput(data?.workOrder?.dueDate || data?.workOrder?.createdAt));
  }, [data?.workOrder?.createdAt, data?.workOrder?.dueDate]);

  // More robust check that handles different casing, etc.
  const checkUserRole = (user, role) => {
    if (!user || !user.role) return false;
    // Case insensitive compare and strip any whitespace
    return user.role.trim().toUpperCase() === role.trim().toUpperCase();
  };



  const isCallCenter = currentUser && checkUserRole(currentUser, 'CALL_CENTER');

  // Add permission check for admin actions (edit, delete)
  const canPerformAdminActions = currentUser && (
    checkUserRole(currentUser, 'ADMIN') || 
    checkUserRole(currentUser, 'HOUSEKEEPER_MANAGER') ||
    checkUserRole(currentUser, 'PROPERTY_MANAGER')
  );

    // Permission check for assignment actions (assign work orders)
  const canAssignWorkOrders = currentUser && (
    checkUserRole(currentUser, 'ADMIN') ||
    checkUserRole(currentUser, 'PROPERTY_MANAGER') ||
    checkUserRole(currentUser, 'HOUSEKEEPER_MANAGER')
  );
  const canManageGuestAlerts = currentUser && (
    checkUserRole(currentUser, 'ADMIN') ||
    checkUserRole(currentUser, 'PROPERTY_MANAGER') ||
    checkUserRole(currentUser, 'HOUSEKEEPER_MANAGER') ||
    checkUserRole(currentUser, 'CALL_CENTER')
  );
  
  // console.log('WorkOrderDetail: canAssignWorkOrders:', canAssignWorkOrders, 'currentUser role:', currentUser?.role);

  // Add this to handle assignment success
  const handleAssignmentSuccess = () => {
    // console.log('WorkOrderDetail: handleAssignmentSuccess called');
    setIsAssignModalOpen(false);
    toast.success('Work order assigned successfully');
    // console.log('WorkOrderDetail: Calling refetch...');
    refetch().then(() => {
      // console.log('WorkOrderDetail: Refetch completed');
    }).catch((error) => {
      // console.error('WorkOrderDetail: Refetch failed:', error);
    });
  };

  const handleStatusChange = async (newStatus) => {
    // console.log('WorkOrderDetail: Changing status to:', newStatus);
    // console.log('WorkOrderDetail: Current user:', currentUser);
    // console.log('WorkOrderDetail: User ID:', currentUser?.id);
    
    try {
      await updateWorkOrderStatus({
        variables: {
          id,
          status: newStatus,
          acceptedById: newStatus === 'IN_PROGRESS' ? currentUser?.id : undefined
        }
      });
    } catch (err) {
      // console.error('Error updating work order status:', err);
      setErrorMessage('Failed to update status. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this work order? This action cannot be undone.')) {
      try {
        await deleteWorkOrder({
          variables: { id }
        });
      } catch (err) {
        // console.error('Error deleting work order:', err);
        setErrorMessage('Failed to delete work order. Please try again.');
      }
    }
  };

  const handleRestoreWorkOrder = async () => {
    if (window.confirm('Are you sure you want to restore this work order? It will be reopened and moved back to active work orders.')) {
      try {
        await updateWorkOrderStatus({
          variables: {
            id,
            status: 'OPEN'
          }
        });
        toast.success('Work order restored successfully');
      } catch (err) {
        setErrorMessage('Failed to restore work order. Please try again.');
      }
    }
  };

  const handleRestoreDeletedWorkOrder = async () => {
    if (window.confirm('Are you sure you want to restore this deleted work order? It will be reopened and visible again in active work orders.')) {
      try {
        await restoreWorkOrder({
          variables: { id }
        });
      } catch (err) {
        setErrorMessage('Failed to restore work order. Please try again.');
      }
    }
  };

  const handleQuickComplete = async () => {
    if (!window.confirm('Quick complete this work order without adding time or completion notes?')) {
      return;
    }

    setIsQuickCompleting(true);
    setErrorMessage('');

    try {
      await updateWorkOrderStatus({
        variables: {
          id,
          status: 'COMPLETED'
        }
      });
      toast.success('Work order completed');
    } catch (err) {
      setErrorMessage('Failed to complete work order. Please try again.');
    } finally {
      setIsQuickCompleting(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotoFiles(files);
    setErrorMessage('');
  };

  const convertHeicToJpeg = async (file) => {
    const heic2anyModule = await import('heic2any');
    const heic2any = heic2anyModule.default;
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.8,
      timeout: 30000,
    });
    const convertedBlob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    if (!convertedBlob) throw new Error('Conversion resulted in an empty file');
    return new File(
      [convertedBlob],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
  };

  const isHeicFile = (file) => (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  );

  const handlePhotoUpload = async (e) => {
    e.preventDefault();

    if (!photoFiles.length) {
      setErrorMessage('Please select at least one photo to upload.');
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setUploadProgress(0);

    const totalFiles = photoFiles.length;
    const uploadedUrls = [];

    try {
      for (let i = 0; i < totalFiles; i++) {
        const original = photoFiles[i];
        let processedFile = original;

        if (isHeicFile(original)) {
          toast.loading(
            `Converting iPhone HEIC image (${i + 1}/${totalFiles})...`,
            { id: 'heic-conversion' }
          );
          try {
            processedFile = await convertHeicToJpeg(original);
            toast.success('HEIC image converted successfully', { id: 'heic-conversion' });
          } catch (conversionError) {
            toast.dismiss('heic-conversion');
            setErrorMessage(
              `Unable to convert HEIC image "${original.name}". Please convert it to JPEG or PNG before uploading.`
            );
            setIsUploading(false);
            return;
          }
        }

        const imageUrl = await uploadWorkOrderPhoto({
          apiUrl: API_URL,
          file: processedFile,
          retries: 1,
          onProgress: (progress) => {
            setUploadProgress(((i + (progress / 100)) / totalFiles) * 100);
          },
        });

        uploadedUrls.push(imageUrl);
      }

      const currentPhotos = data?.workOrder?.photos || [];
      const updatedPhotos = [...currentPhotos, ...uploadedUrls];

      await updateWorkOrder({
        variables: {
          id,
          input: { id, photos: updatedPhotos },
        },
      });

      setUploadProgress(100);
      setPhotoFiles([]);
      const photoInput = document.getElementById('photo');
      if (photoInput) photoInput.value = '';
      refetch();
      toast.success(
        `${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} uploaded successfully`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to upload one or more images. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    // Prevent scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
    // Restore scrolling
    document.body.style.overflow = 'auto';
  }, []);

  const handlePrevPhoto = useCallback((e) => {
    e.stopPropagation();
    if (data?.workOrder?.photos && data.workOrder.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === 0 ? data.workOrder.photos.length - 1 : prevIndex - 1
      );
    }
  }, [data?.workOrder?.photos]);

  const handleNextPhoto = useCallback((e) => {
    e.stopPropagation();
    if (data?.workOrder?.photos && data.workOrder.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === data.workOrder.photos.length - 1 ? 0 : prevIndex + 1
      );
    }
  }, [data?.workOrder?.photos]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!lightboxOpen) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        handlePrevPhoto(e);
        break;
      case 'ArrowRight':
        handleNextPhoto(e);
        break;
      case 'Escape':
        handleCloseLightbox();
        break;
      default:
        break;
    }
  }, [lightboxOpen]);

  // Add event listener for keyboard navigation
  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Ensure overflow is restored when component unmounts
      document.body.style.overflow = 'auto';
    };
  }, [lightboxOpen, data, handleKeyDown]);

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 p-4 rounded-md">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">Error loading work order</h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{error.message}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const workOrder = data?.workOrder;
  if (!workOrder) return null;

  const displayedDueDate = workOrder.dueDate || workOrder.createdAt;
  const propertyAddress = formatPropertyAddress(workOrder.property);
  const encodedPropertyAddress = encodeURIComponent(propertyAddress);

  const openDueDateEditor = () => {
    setDueDateDraft(formatDateForInput(workOrder.dueDate || workOrder.createdAt));
    setIsDueDateEditorOpen(true);
  };

  const closeDueDateEditor = () => {
    setDueDateDraft(formatDateForInput(workOrder.dueDate || workOrder.createdAt));
    setIsDueDateEditorOpen(false);
  };

  const saveDueDate = async (dateKey) => {
    const selectedDateKey = dateKey !== undefined
      ? dateKey
      : dueDateInputRef.current?.value || dueDateDraft;

    setIsSavingDueDate(true);
    setErrorMessage('');

    try {
      await updateWorkOrder({
        variables: {
          id,
          input: {
            id,
            dueDate: selectedDateKey ? createDueDateValue(selectedDateKey) : null,
          },
        },
      });
      await refetch();
      onDataChange?.();
      setIsDueDateEditorOpen(false);
      toast.success(selectedDateKey ? 'Due date updated' : 'Due date cleared');
    } catch (err) {
      setErrorMessage('Failed to update due date. Please try again.');
    } finally {
      setIsSavingDueDate(false);
    }
  };

  const toggleGuestAlerts = async () => {
    const enabling = !workOrder.guestAlertsEnabled;
    const message = enabling
      ? 'Resume future guest alerts for this work order? Missed alerts will not be sent retroactively.'
      : 'Stop all future guest alerts for this work order? Messages already sent cannot be recalled.';
    if (!window.confirm(message)) return;

    try {
      await updateWorkOrder({
        variables: {
          id,
          input: { id, guestAlertsEnabled: enabling },
        },
      });
      await refetch();
      onDataChange?.();
      toast.success(enabling ? 'Future guest alerts resumed' : 'Future guest alerts stopped');
    } catch (guestAlertError) {
      toast.error(guestAlertError.message || 'Unable to update guest alerts');
    }
  };

  // Find the current or most recent reservation for guest contact info
  const getCurrentReservation = () => {
    // First, try to use the reservation from the work order if it has contact info
    if (workOrder.reservation?.email || workOrder.reservation?.mobile_phone) {
      return workOrder.reservation;
    }
    
    // Otherwise, find current or most recent reservation from property reservations
    const reservations = propertyReservationsData?.propertyReservations || [];
    const now = new Date();
    
    // Find current reservation (check-in <= now <= check-out)
    const currentReservation = reservations.find(res => {
      const checkIn = new Date(res.check_in);
      const checkOut = new Date(res.check_out);
      return checkIn <= now && now <= checkOut && !res.is_cancelled;
    });
    
    if (currentReservation) return currentReservation;
    
    // Find most recent past reservation with contact info
    const pastReservations = reservations
      .filter(res => new Date(res.check_out) < now && !res.is_cancelled && (res.email || res.mobile_phone))
      .sort((a, b) => new Date(b.check_out) - new Date(a.check_out));
    
    if (pastReservations.length > 0) return pastReservations[0];
    
    // Find next upcoming reservation with contact info
    const upcomingReservations = reservations
      .filter(res => new Date(res.check_in) > now && !res.is_cancelled && (res.email || res.mobile_phone))
      .sort((a, b) => new Date(a.check_in) - new Date(b.check_in));
    
    return upcomingReservations.length > 0 ? upcomingReservations[0] : null;
  };

  const currentReservation = getCurrentReservation();

  // Determine if there's an active current reservation (guest currently staying)
  const getActiveCurrentReservation = () => {
    const reservations = propertyReservationsData?.propertyReservations || [];
    const now = new Date();
    return reservations.find(res => {
      const checkIn = new Date(res.check_in);
      const checkOut = new Date(res.check_out);
      return checkIn <= now && now <= checkOut && !res.is_cancelled;
    }) || null;
  };

  // Find the next incoming reservation (first non-cancelled where check_in > now)
  const getNextIncomingReservation = () => {
    const reservations = propertyReservationsData?.propertyReservations || [];
    const now = new Date();
    return reservations
      .filter(res => new Date(res.check_in) > now && !res.is_cancelled)
      .sort((a, b) => new Date(a.check_in) - new Date(b.check_in))[0] || null;
  };

  const activeCurrentReservation = getActiveCurrentReservation();
  const nextIncomingReservation = getNextIncomingReservation();

  // Converts a 24-hour time string (e.g., "16:00") to 12-hour format (e.g., "4:00 PM")
  const formatTimeTo12Hour = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (isNaN(hours)) return timeStr;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${String(minutes || 0).padStart(2, '0')} ${period}`;
  };

  // Formats a reservation check-in date + time for display
  const formatCheckInDisplay = (reservation) => {
    const checkInDate = format(new Date(reservation.check_in), 'MMM d, yyyy');
    if (reservation.has_time_override && reservation.override_check_in) {
      return `${checkInDate} - ${format(new Date(reservation.override_check_in), 'h:mm a')}`;
    }
    const rawTime = reservation.check_in_time || workOrder.property?.checkInTime || null;
    const displayTime = formatTimeTo12Hour(rawTime);
    return displayTime ? `${checkInDate} - ${displayTime}` : checkInDate;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div className="flex flex-col space-y-2 sm:space-y-0">
          {!isSlidePanel && (
            <Link to="/dashboard/work-orders" className="inline-flex items-center text-gray-400 hover:text-white sm:text-gray-600 sm:hover:text-gray-900 w-fit">
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Work Orders
            </Link>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
            <h1 className={`text-2xl font-semibold ${isSlidePanel ? 'text-white' : 'text-white sm:text-gray-900'}`}>Work Order Details</h1>
            <div className="flex flex-wrap items-center gap-1 mt-2 sm:mt-0">
              {workOrder.customFlags?.map(flag => (
                <CustomFlagIndicator key={flag.id} flag={flag} />
              ))}
              {workOrder.status !== 'OPEN' && (
                <WorkOrderBadge className={getStatusColor(workOrder.status)}>
                  {workOrder.status.replace('_', ' ')}
                </WorkOrderBadge>
              )}
              {workOrder.streamlineWorkOrderId && (
                <WorkOrderBadge className="bg-yellow-100 text-yellow-800">
                  <svg className="mr-1 h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Streamline
                </WorkOrderBadge>
              )}
              {workOrder.isGuestSubmitted && (
                <WorkOrderBadge className="bg-emerald-100 text-emerald-800">
                  <svg className="mr-1 h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Guest Submitted
                </WorkOrderBadge>
              )}
              {workOrder.guestAlertsEnabled && (
                <WorkOrderBadge className="bg-teal-100 text-teal-800">
                  Guest Alerts On
                </WorkOrderBadge>
              )}
              {!workOrder.guestAlertsEnabled && workOrder.guestAlertsDisabledAt && (
                <WorkOrderBadge className="bg-gray-200 text-gray-700">
                  Guest Alerts Stopped
                </WorkOrderBadge>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          {canManageGuestAlerts && workOrder.reservationId && (workOrder.guestAlertsEnabled || workOrder.guestAlertsDisabledAt) && (
            <button
              type="button"
              onClick={toggleGuestAlerts}
              className={`inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium ${
                workOrder.guestAlertsEnabled
                  ? 'border-red-300 bg-white text-red-700 hover:bg-red-50'
                  : 'border-teal-300 bg-white text-teal-700 hover:bg-teal-50'
              }`}
            >
              {workOrder.guestAlertsEnabled ? 'Stop Future Guest Alerts' : 'Resume Guest Alerts'}
            </button>
          )}
          {/* Add Assign button for admin/property manager/housekeeping manager */}
          {canAssignWorkOrders && (
            <button
              onClick={() => {
                // console.log('Assign button clicked, opening modal...');
                setIsAssignModalOpen(true);
              }}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Assign
            </button>
          )}

          {canPerformAdminActions && (
            isSlidePanel ? (
              <a
                href={`/dashboard/work-orders/${id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
                <svg className="h-3 w-3 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ) : (
              <Link
                to={`/dashboard/work-orders/${id}/edit`}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
              </Link>
            )
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 p-3 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {workOrder.isDeleted && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-red-800">This work order has been deleted</h3>
                <div className="mt-1 text-sm text-red-700 space-y-1">
                  {workOrder.deletedAt && (
                    <p>Deleted on {format(new Date(workOrder.deletedAt), 'MMM d, yyyy h:mm a')}</p>
                  )}
                  {workOrder.deletedBy && (
                    <p>
                      Deleted by{' '}
                      <span className="font-medium">
                        {workOrder.deletedBy.firstName} {workOrder.deletedBy.lastName}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            {canPerformAdminActions && (
              <button
                onClick={handleRestoreDeletedWorkOrder}
                className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Restore Work Order
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
            <div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <h3 className="w-full text-lg leading-6 font-medium text-gray-900 sm:w-auto">
                  {workOrder.property?.name}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <div
                    className="flex items-center whitespace-nowrap px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                    title={workOrder.dueDate ? 'Work order due date' : 'No due date set; showing created date'}
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Due: {formatDueDate(displayedDueDate)}
                  </div>
                  <button
                    type="button"
                    onClick={openDueDateEditor}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label="Edit work order due date"
                    title="Edit due date"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              </div>
              {isDueDateEditorOpen && (
                <div className="mt-3 flex flex-col gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 sm:flex-row sm:items-center">
                  <label htmlFor="inline-due-date" className="text-sm font-medium text-blue-900">
                    Due date
                  </label>
                  <input
                    id="inline-due-date"
                    ref={dueDateInputRef}
                    type="date"
                    value={dueDateDraft}
                    onChange={(event) => setDueDateDraft(event.target.value)}
                    onInput={(event) => setDueDateDraft(event.target.value)}
                    className="w-full rounded-md border border-blue-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 sm:w-auto"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => saveDueDate()}
                      disabled={isSavingDueDate}
                      className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingDueDate ? 'Saving...' : 'Save'}
                    </button>
                    {workOrder.dueDate && (
                      <button
                        type="button"
                        onClick={() => saveDueDate('')}
                        disabled={isSavingDueDate}
                        className="inline-flex items-center justify-center rounded-md border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={closeDueDateEditor}
                      disabled={isSavingDueDate}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {propertyAddress && (
                <div className="mt-3 space-y-3">
                  <p className="max-w-2xl text-sm text-gray-500">
                    {propertyAddress}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    <a
                      href={`https://maps.apple.com/?daddr=${encodedPropertyAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
                    >
                      <svg className="mr-1.5 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Apple Maps
                    </a>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodedPropertyAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-100 sm:w-auto"
                    >
                      <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Google Maps
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {workOrder.customFlags?.map(flag => (
                <CustomFlagIndicator key={flag.id} flag={flag} />
              ))}
              {workOrder.hasActiveTimer && (
                <WorkOrderBadge
                  className="bg-emerald-100 text-emerald-800"
                  aria-label="Work order timer is currently running"
                >
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  In Progress
                </WorkOrderBadge>
              )}
              {activeCurrentReservation && (
                <WorkOrderBadge
                  className="bg-green-100 text-green-800"
                  aria-label="Property is currently occupied"
                >
                  <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Occupied
                </WorkOrderBadge>
              )}
              {workOrder.isAfterHours && (
                <WorkOrderBadge
                  className="bg-indigo-100 text-indigo-800"
                  aria-label="After Hours work order"
                >
                  <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  After Hours
                </WorkOrderBadge>
              )}
              {workOrder.status === 'IN_PROGRESS' && (
                <WorkOrderBadge className="bg-blue-100 text-blue-800">
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                  Started
                </WorkOrderBadge>
              )}
              {workOrder.status === 'COMPLETED' && (
                <WorkOrderBadge className="bg-green-100 text-green-800">
                  <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Completed
                </WorkOrderBadge>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          {/* GPS-Enabled Work Time Tracker - hidden for call center */}
          {!isCallCenter && (workOrder.status === 'OPEN' || workOrder.status === 'IN_PROGRESS') && (
            <div className="mb-6">
              <WorkOrderGPSTracker
                workOrderId={id}
                workOrderStatus={workOrder.status}
                currentUser={currentUser}
                property={workOrder.property}
                onStatusChange={handleStatusChange}
              />
            </div>
          )}

          {/* Complete Work Order Button - hidden for call center */}
          {!isCallCenter && workOrder.status === 'IN_PROGRESS' && (
            <div className="mb-6">
              <button
                onClick={() => setIsCompleteModalOpen(true)}
                className="w-full inline-flex items-center justify-center px-4 py-3 border-2 border-green-600 text-sm font-semibold rounded-lg text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Complete Work Order
              </button>
            </div>
          )}
          
          {/* Manual Time Logging Section - hidden for call center */}
          {!isCallCenter && (
            <div className="mb-6">
              <WorkOrderTimeTracker 
                workOrderId={id} 
                workOrderStatus={workOrder.status}
                currentUser={currentUser}
                property={workOrder.property}
                onQuickComplete={handleQuickComplete}
                isQuickCompleting={isQuickCompleting}
              />
            </div>
          )}

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8">
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {workOrder.workOrderType && workOrder.workOrderType !== WorkOrderType.GUEST_IMPACT && (
                  <span className="mr-2 mb-2 inline-flex">
                    <WorkOrderBadge className={WORK_ORDER_TYPE_BADGE_CLASSES[workOrder.workOrderType] || 'bg-gray-100 text-gray-800'}>
                      {getWorkOrderTypeOption(workOrder.workOrderType).label}
                    </WorkOrderBadge>
                  </span>
                )}
                {workOrder.isUrgent && (
                  <span className="mr-2 mb-2 inline-flex">
                    <WorkOrderUrgentBadge />
                  </span>
                )}
                {workOrder.description}
              </dd>
            </div>
            
            {/* Assignment Information - New Section */}
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <dt className="text-sm font-medium text-gray-500">Assignment</dt>
              <dd className="mt-1">
                {workOrder.assignedTo ? (
                  <div className="flex items-center text-sm text-gray-900">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-700 mr-2">
                      {workOrder.assignedTo.name?.[0] || '?'}
                    </span>
                    <div>
                      <div className="font-medium">{workOrder.assignedTo.name}</div>
                      <div className="text-gray-500 text-xs">{UserRoleLabels[workOrder.assignedTo.role] || workOrder.assignedTo.role}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not assigned to any user</p>
                )}
                
                {/* Display assigned roles */}
                {workOrder.assignedRoles && workOrder.assignedRoles.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-gray-500 mb-1">Assigned to Roles:</div>
                    <div className="flex flex-wrap gap-1">
                      {workOrder.assignedRoles.map((role) => (
                        <span 
                          key={role}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {UserRoleLabels[role] || role}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </dd>
            </div>
            
            {/* Guest Contact Information */}
            {currentReservation && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Guest Contact</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="space-y-1">
                    <div className="font-medium">{currentReservation.guest_name}</div>
                    {currentReservation !== workOrder.reservation && (
                      <div className="text-xs text-gray-500 mb-1">
                        {(() => {
                          const now = new Date();
                          const checkIn = new Date(currentReservation.check_in);
                          const checkOut = new Date(currentReservation.check_out);
                          
                          if (checkIn <= now && now <= checkOut) {
                            return "Current guest";
                          } else if (checkOut < now) {
                            return `Most recent guest (checked out ${format(checkOut, 'MMM d')})`;
                          } else {
                            return `Upcoming guest (checking in ${format(checkIn, 'MMM d')})`;
                          }
                        })()}
                      </div>
                    )}
                    {currentReservation.mobile_phone && (
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <a href={`tel:${currentReservation.mobile_phone}`} className="text-blue-600 hover:text-blue-800">
                          {currentReservation.mobile_phone}
                        </a>
                      </div>
                    )}
                    {currentReservation.email && (
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <a href={`mailto:${currentReservation.email}`} className="text-blue-600 hover:text-blue-800">
                          {currentReservation.email}
                        </a>
                      </div>
                    )}
                    {!currentReservation.mobile_phone && !currentReservation.email && (
                      <div className="text-gray-500 text-sm">No contact information available</div>
                    )}
                  </div>
                </dd>
              </div>
            )}

            {/* Guest-Submitted Work Order Info */}
            {workOrder.isGuestSubmitted && (
              <div className="sm:col-span-2 border-t pt-4 mt-2">
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <span className="inline-flex items-center px-2 py-0.5 mr-2 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    Guest Submitted
                  </span>
                  Submitted via Guest App
                </dt>
                <dd className="mt-2 text-sm text-gray-900">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-1">
                    {workOrder.guestName && (
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-2 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">{workOrder.guestName}</span>
                      </div>
                    )}
                    {workOrder.guestPhone && (
                      <div className="flex items-center">
                        <svg className="h-4 w-4 mr-2 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <a href={`tel:${workOrder.guestPhone}`} className="text-emerald-700 hover:text-emerald-900">
                          {workOrder.guestPhone}
                        </a>
                      </div>
                    )}
                  </div>
                </dd>
              </div>
            )}

            {/* Reservation Status */}
            {propertyReservationsData && (
              <div className="sm:col-span-2 border-t pt-4 mt-2">
                <dt className="text-sm font-medium text-gray-500 mb-2">Reservation Status</dt>
                <dd className="mt-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current Reservation */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className={`h-5 w-5 ${activeCurrentReservation ? 'text-green-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500">Current Reservation</div>
                        {activeCurrentReservation ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">{activeCurrentReservation.guest_name}</div>
                            <div className="text-xs text-gray-500">
                              {format(new Date(activeCurrentReservation.check_in), 'MMM d')} → {format(new Date(activeCurrentReservation.check_out), 'MMM d, yyyy')}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">No current reservation</div>
                        )}
                      </div>
                    </div>

                    {/* Next Incoming Reservation */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className={`h-5 w-5 ${nextIncomingReservation ? 'text-blue-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500">Next Incoming Reservation</div>
                        {nextIncomingReservation ? (
                          <div className="text-sm font-medium text-gray-900">
                            {formatCheckInDisplay(nextIncomingReservation)}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">No upcoming reservations</div>
                        )}
                      </div>
                    </div>
                  </div>
                </dd>
              </div>
            )}

            {/* Property Manager Information */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Property Management</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <PropertyManagementDisplay
                  team={propertyManagementTeam}
                  managers={propertyManagers}
                />
              </dd>
            </div>

            {/* Property Access Information */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Access Details</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {workOrder.property?.entryDetails ? (
                  <div className="space-y-1">
                    <p className="whitespace-pre-line">{workOrder.property.entryDetails}</p>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No entry details available</div>
                )}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Submitted By</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {workOrder.submittedBy ? (
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 mr-2">
                      {workOrder.submittedBy.firstName?.[0]}{workOrder.submittedBy.lastName?.[0] || ''}
                    </span>
                    <div>
                      <div>{workOrder.submittedBy.name}</div>
                      {workOrder.submittedBy.role && (
                        <div className="text-xs text-gray-500 capitalize">{workOrder.submittedBy.role.toLowerCase()}</div>
                      )}
                    </div>
                  </div>
                ) : 'Unknown'}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {format(new Date(workOrder.createdAt), 'MMM d, yyyy h:mm a')}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {format(new Date(workOrder.updatedAt), 'MMM d, yyyy h:mm a')}
              </dd>
            </div>

            {workOrder.completedAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Completed On</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-green-600">
                      {format(new Date(workOrder.completedAt), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                </dd>
              </div>
            )}
            
            {workOrder.dueDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="flex items-center">
                    <svg className="h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium text-blue-600">
                      {formatDueDate(workOrder.dueDate)}
                    </span>
                  </div>
                </dd>
              </div>
            )}

            {/* Media Section - Photos & Videos Combined */}
            <div className="sm:col-span-2 border-t pt-4 mt-2">
              <dt className="text-sm font-medium text-gray-500 mb-2">Media</dt>
              <dd>
                {/* Photos and Videos Grid */}
                <div className="flex flex-wrap gap-3">
                  {/* Photos */}
                  {workOrder.photos && workOrder.photos.map((photo, index) => (
                    <div key={`photo-${index}`} className="relative group">
                      <div className="h-24 w-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                        <img 
                          src={photo} 
                          alt={`Work order photo ${index + 1}`} 
                          className="h-full w-full object-cover cursor-pointer"
                          onClick={() => handleOpenLightbox(index)}
                        />
                      </div>
                      {/* Photo icon badge */}
                      <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      {/* Remove button for photos */}
                      <button
                        type="button"
                        onClick={async () => {
                          const updatedPhotos = workOrder.photos.filter((_, i) => i !== index);
                          try {
                            await updateWorkOrder({
                              variables: {
                                id,
                                input: { id, photos: updatedPhotos }
                              }
                            });
                            refetch();
                            toast.success('Photo removed');
                          } catch (error) {
                            toast.error('Failed to remove photo');
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        aria-label="Remove photo"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  {/* Videos */}
                  {workOrder.videos && workOrder.videos.map((video) => (
                    <div key={`video-${video.id}`} className="relative group">
                      <div 
                        className="h-24 w-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setSelectedVideo(video);
                          setIsVideoModalOpen(true);
                        }}
                      >
                        <img
                          src={video.thumbnailUrl}
                          alt="Video thumbnail"
                          className="h-full w-full object-cover"
                        />
                        {/* Play icon overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-8 h-8 bg-black bg-opacity-60 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                        {/* Duration badge */}
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-xs px-1.5 py-0.5 rounded">
                          {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                        </div>
                      </div>
                      {/* Video icon badge */}
                      <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5">
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      {/* Remove button for videos */}
                      <button
                        type="button"
                        onClick={async () => {
                          const updatedVideos = workOrder.videos.filter(v => v.id !== video.id);
                          try {
                            await updateWorkOrder({
                              variables: {
                                id,
                                input: { id, videos: updatedVideos }
                              }
                            });
                            refetch();
                            toast.success('Video removed');
                          } catch (error) {
                            toast.error('Failed to remove video');
                          }
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        aria-label="Remove video"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* Empty state */}
                {(!workOrder.photos || workOrder.photos.length === 0) && (!workOrder.videos || workOrder.videos.length === 0) && (
                  <p className="text-sm text-gray-500 mb-4">No media attached</p>
                )}
                
                {/* Photo Upload */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-2">Add Photos</p>
                  <form onSubmit={handlePhotoUpload} className="flex items-center">
                    <input
                      type="file"
                      id="photo"
                      accept="image/*,.heic,.heif"
                      multiple
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-medium
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100
                      "
                    />
                    <button
                      type="submit"
                      disabled={!photoFiles.length || isUploading}
                      className={`ml-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                        !photoFiles.length || isUploading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isUploading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          {photoFiles.length > 1 ? `Upload ${photoFiles.length}` : 'Upload'}
                        </>
                      )}
                    </button>
                  </form>

                  {photoFiles.length > 0 && !isUploading && (
                    <p className="text-xs text-gray-500 mt-2">
                      {photoFiles.length} file{photoFiles.length > 1 ? 's' : ''} selected
                    </p>
                  )}

                  {isUploading && (
                    <div className="mt-2">
                      <div className="bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">{Math.round(uploadProgress)}%</p>
                    </div>
                  )}
                </div>
                
                {/* Video Upload */}
                {(!workOrder.videos || workOrder.videos.length < 2) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">Add Video ({workOrder.videos?.length || 0}/2)</p>
                    <VideoUploader
                      videos={workOrder.videos || []}
                      onVideosChange={async (videos) => {
                        try {
                          await updateWorkOrder({
                            variables: {
                              id,
                              input: { id, videos }
                            }
                          });
                          refetch();
                        } catch (error) {
                          toast.error('Failed to update videos');
                        }
                      }}
                      maxVideos={2}
                      hideExistingVideos={true}
                    />
                  </div>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal
        video={selectedVideo}
        isOpen={isVideoModalOpen}
        onClose={() => {
          setIsVideoModalOpen(false);
          setSelectedVideo(null);
        }}
      />

      {/* Work Order Comment Thread - Replaces old escalation/approval system */}
      <WorkOrderCommentThread
        workOrderId={id}
        workOrder={workOrder}
        currentUser={currentUser}
      />

      {/* Work Order Expenses Section */}
      <WorkOrderExpensesSection
        workOrderId={id}
        currentUser={currentUser}
        canEdit={!!currentUser} // All authenticated users can add expenses
        workOrderStatus={workOrder.status}
      />

      <div className="mt-6">
        <WorkOrderFlagManager
          workOrder={workOrder}
          onFlagChange={() => {
            refetch();
            onDataChange?.();
          }}
        />
      </div>

      {/* Work Order Actions Section - Moved to bottom */}
      {canPerformAdminActions && (
        <div className="bg-white shadow overflow-hidden rounded-lg mt-6">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Work Order Actions
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {workOrder.status === 'CANCELED' 
                ? 'This work order is canceled. You can restore it to reopen or permanently delete it.'
                : 'Choose an action for this work order. These actions have permanent effects.'
              }
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Restore Work Order - Only show for canceled work orders */}
              {workOrder.status === 'CANCELED' && (
                <div className="border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Restore Work Order</h4>
                      <p className="mt-1 text-sm text-gray-500">
                        Reopen this canceled work order and move it back to active work orders. The work order will be restored to "Open" status and can be assigned and completed normally.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={handleRestoreWorkOrder}
                          className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Restore to Open
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cancel Work Order - Only show for open/in-progress work orders */}
              {(workOrder.status === 'OPEN' || workOrder.status === 'IN_PROGRESS') && (
                <div className="border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Cancel Work Order</h4>
                      <p className="mt-1 text-sm text-gray-500">
                        Mark this work order as canceled. The work order will be kept in the system for records but moved to "Past Work Orders" with canceled status. This can be used when work is no longer needed but you want to keep a record.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => handleStatusChange('CANCELED')}
                          className="inline-flex items-center px-3 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Cancel Work Order
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Work Order - Always show for admin actions */}
              <div className="border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-gray-900">Delete Work Order</h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Permanently remove this work order from the system. All data including photos, assignments, and history will be lost forever. This action cannot be undone and should only be used for work orders created by mistake.
                    </p>
                    <div className="mt-3">
                      <button
                        onClick={handleDelete}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete Permanently
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Work Order - Call Center only (not shown if admin actions are already visible) */}
      {isCallCenter && !canPerformAdminActions && (workOrder.status === 'OPEN' || workOrder.status === 'IN_PROGRESS') && (
        <div className="bg-white shadow overflow-hidden rounded-lg mt-6">
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Cancel Work Order</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Mark this work order as canceled. The work order will be kept in the system for records but moved to "Past Work Orders" with canceled status.
                  </p>
                  <div className="mt-3">
                    <button
                      onClick={() => handleStatusChange('CANCELED')}
                      className="inline-flex items-center px-3 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100"
                      aria-label="Cancel work order"
                      tabIndex={0}
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Cancel Work Order
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && workOrder.photos && workOrder.photos.length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={handleCloseLightbox}
        >
          <div className="relative max-w-4xl w-full max-h-full">
            {/* Close button */}
            <button 
              className="absolute right-4 top-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
              onClick={handleCloseLightbox}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <div className="relative">
              <img 
                src={workOrder.photos[currentPhotoIndex]} 
                alt={`Work order photo ${currentPhotoIndex + 1}`} 
                className="max-h-[80vh] mx-auto object-contain"
              />
              
              {/* Image counter */}
              <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black bg-opacity-50 py-1">
                {currentPhotoIndex + 1} / {workOrder.photos.length}
              </div>
            </div>
            
            {/* Navigation buttons */}
            {workOrder.photos.length > 1 && (
              <>
                <button 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handlePrevPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handleNextPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assign Work Order Modal */}
      {isAssignModalOpen && (
        <AssignWorkOrderModal 
          workOrder={workOrder}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={handleAssignmentSuccess}
        />
      )}

      {/* Complete Work Order Modal */}
      {isCompleteModalOpen && (
        <CompleteWorkOrderModal
          workOrder={workOrder}
          onClose={() => setIsCompleteModalOpen(false)}
          onComplete={() => {
            setIsCompleteModalOpen(false);
            refetch();
            onDataChange?.();
          }}
        />
      )}
    </div>
  );
}
